import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Card, Button } from 'react-bootstrap';
import { Formik, Form, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { isUndefined, isNull } from 'lodash';

import { BackButton, FormGroupControl, Loading, LoadingError, NoData } from '@components/Base';
import { InsuranceTypes } from '@models/InsuranceType';

import { FormValues, formValuesSchema } from './schema';
import { useInitialValues } from './hook';
import { policyFetch, carClaimCreate } from './action';
import { selectCarPolicyClaim } from './selector';
import { CarClaimCreatePayload } from './carClaimCreate';

import { PolicyOverviewCardBody } from '../HealthPolicyClaim/policy-overview-card-body';
import { AccidentDetailsCardBody } from './accident-details-card-body';
import { PaymentInfoCardBody } from '../HealthPolicyClaim/payment-info-card-body';
import { ClaimCardBody } from './claim-card-body';

const CarPolicyClaim = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { orderableId, orderableType } = useParams();
  const { policy } = useSelector(selectCarPolicyClaim);
  const initialValues = useInitialValues();
  const isLoaded = !isUndefined(policy);
  const isError = isNull(policy);
  const hasPolicy = isLoaded && !isNull(policy);

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const payload: CarClaimCreatePayload = {
      ...values,
      carOrderId: Number(orderableId),
      insuranceType: InsuranceTypes.Car,
    };
    dispatch(carClaimCreate(payload));
    setSubmitting(false);
  };

  useEffect(() => {
    dispatch(policyFetch({ orderableId, orderableType }));
  }, [orderableId, orderableType]);

  return (
    <>
      <BackButton className="mb-4" />
      {!isLoaded && <Loading />}
      {isError && <LoadingError />}
      {isLoaded && !hasPolicy && <NoData />}

      {hasPolicy && initialValues && (
        <Card>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={formValuesSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form>
                <Card.Header className="fs-5 fw-bold">{t('common:claimForm')}</Card.Header>

                <PolicyOverviewCardBody />
                <AccidentDetailsCardBody />
                <PaymentInfoCardBody />
                <ClaimCardBody />

                <Card.Body>
                  <FormGroupControl
                    type="checkbox"
                    name="acceptTermsAndConditions"
                    label={t('carInsuranceClaimForm.acceptTermsAndConditions')}
                    bsPrefix="form-check-input"
                    groupProps={{ className: 'form-check form-check-inline' }}
                    required
                  />
                </Card.Body>

                <Card.Footer className="bg-white">
                  <div className="text-center my-4">
                    <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
                      {t('common:submit')}
                    </Button>
                  </div>
                </Card.Footer>
              </Form>
            )}
          </Formik>
        </Card>
      )}
    </>
  );
};

export { CarPolicyClaim };
